import { useEffect, useMemo, useState } from 'react';

/**
 * Slices a client-side list for table paging: page / page size, 1-based range
 * for display, and handlers suitable for `DataTableToolbar`.
 */
export function useClientListPagination<T>({
  items,
  initialPageSize = 25,
  filterKey,
}: {
  readonly items: readonly T[];
  readonly initialPageSize?: number;
  /**
   * Any string that should bump the list back to page 1 when it changes
   * (e.g. search query + filter values, or route “mine” vs “all”).
   */
  readonly filterKey: string;
}): {
  readonly pageItems: readonly T[];
  readonly from: number;
  readonly to: number;
  readonly total: number;
  readonly pageSize: number;
  readonly onPageSizeChange: (size: number) => void;
  readonly onPagePrevious: () => void;
  readonly onPageNext: () => void;
  readonly canGoPrevious: boolean;
  readonly canGoNext: boolean;
} {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const total = items.length;
  const maxPage = total === 0 ? 1 : Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    setPage(1);
  }, [filterKey]);

  useEffect(() => {
    if (total > 0 && page > maxPage) {
      setPage(maxPage);
    }
  }, [total, page, maxPage, pageSize]);

  const currentPage = total === 0 ? 1 : Math.min(page, maxPage);
  const start = total === 0 ? 0 : (currentPage - 1) * pageSize;
  const end = total === 0 ? 0 : Math.min(start + pageSize, total);
  const pageItems = useMemo(
    () => (total === 0 ? [] : items.slice(start, end)),
    [end, items, start, total],
  );
  const from = total === 0 ? 0 : start + 1;
  const to = total === 0 ? 0 : end;
  const canGoPrevious = total > 0 && currentPage > 1;
  const canGoNext = total > 0 && currentPage < maxPage;

  return {
    pageItems,
    from,
    to,
    total,
    pageSize,
    onPageSizeChange: (size: number) => {
      setPageSize(size);
      setPage(1);
    },
    onPagePrevious: () => {
      setPage((p) => Math.max(1, p - 1));
    },
    onPageNext: () => {
      setPage((p) => Math.min(maxPage, p + 1));
    },
    canGoPrevious,
    canGoNext,
  };
}
